/**
 * RosterWorkspace — the whole board-first Shift Planner experience, shared by
 * both homes (the Home detail pane and the standalone apps/roster page) so they
 * cannot drift. The board is the agent's face; the plan is the hero; the
 * planner conversation is a demoted slide-in.
 *
 * States:
 *   - empty  -> the team-scaffold grid + a centred "Dienstplan erstellen" hero,
 *               which OPENS THE CONVERSATION. The solve is `:solve` on the
 *               planner MCP server: the agent turn is the operation and the turn
 *               is its clock, so there is no request here with a timer inside it.
 *   - draft  -> the proposal is on the board and the banner says it is not the
 *               ward's answer yet. Publish makes it one, discard throws it away;
 *               both address the whole proposal by its id, in ONE write, because
 *               a ward must never read half a roster.
 *   - review -> the live roster and its status banner; a lead may take one
 *               shift off it, which cancels that shift row.
 *
 * The publish/draft split is real: the draft is the plan's unpublished items,
 * and a shift row exists only once the plan is published. There is no undo
 * stack — the roster is rows, not an append-only command log, so a
 * cancellation is a write the audit trail records.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, Sparkles, MessageCircle, Send, Trash2 } from 'lucide-react'

import type { SessionResponse } from '@/api/types_session'
import type { SessionDetail } from '@/hooks/useSessions'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

import { useRosterController } from './useRosterController'
import { HorizonControl } from './HorizonControl'
import { StatusBanner } from './StatusBanner'
import { RosterBoard } from './RosterBoard'
import { RosterEmptyState } from './RosterEmptyState'
import { TroiRefinePanel } from './TroiRefinePanel'
import { formatRange } from './rosterModel'

export function RosterWorkspace({
  session,
  onSessionCreated,
}: {
  session?: SessionDetail
  onSessionCreated?: (session: SessionResponse) => void
}) {
  const { t, i18n } = useTranslation()
  const c = useRosterController(session, onSessionCreated)

  const [troiOpen, setTroiOpen] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  // The empty-state hero headline is a clean invite, never the raw session title:
  // a launcher-created session defaults to "New Session" (English), which would
  // leak into the German board. Always the localized "Neuer Dienstplan".
  const title = t('roster.newRoster', 'New roster')
  const rangeLabel = formatRange(c.horizon.from, c.horizon.to, i18n.language)
  const boardBusy = c.streaming.isStreaming || c.isSaving || c.isPublishing || c.isDiscarding

  return (
    <div className="flex h-full flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      {/* Control bar — horizon + which roster (all users); the planner
          conversation (planner only). */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <HorizonControl horizon={c.horizon} locale={i18n.language} />
          {/* WHICH roster. A shift belongs to a TEAM, so this is a question with
              an answer rather than a default — shown only when the caller has a
              choice to make. */}
          {c.teams.length > 1 && (
            <label className="flex items-center gap-1.5 text-[13px] text-[var(--color-surface-mute)]">
              <span className="sr-only">{t('roster.team.pick', 'Team')}</span>
              <select
                value={c.teamId ?? ''}
                onChange={(e) => c.setTeamId(e.target.value)}
                className="min-h-9 rounded-[10px] border border-[var(--color-surface-border)] bg-[var(--color-surface-canvas,#ffffff)] px-2 text-[13px] text-[var(--color-surface-ink)]"
              >
                {c.teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {c.isAuthor && (
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTroiOpen(true)}
              aria-label={t('roster.troi.open', 'Ask Troi')}
              className="min-h-9 gap-1.5 px-2.5 text-[13px]"
            >
              <MessageCircle className="h-4 w-4 text-[var(--color-terracotta-500)]" aria-hidden="true" />
              <span className="hidden sm:inline">{t('roster.troi.open', 'Ask Troi')}</span>
            </Button>
          </div>
        )}
      </div>

      {/* Read-only banner — shown to a shared viewer (icon + label, not
          colour). The wire carries no author NAME (created_by.performer is an
          id), so the banner says what the viewer can do, not who shared. */}
      {!c.isAuthor && (
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-neutral-50)] px-4 py-2 text-[13px] text-[var(--color-surface-mute)]">
          <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('roster.viewOnly', 'View only')}</span>
        </div>
      )}

      {/* Status banner — only once a roster exists (nothing to assess when empty). */}
      {c.hasRoster && (
        <div className="shrink-0">
          <StatusBanner
            occurrencesUnavailable={c.occurrencesUnavailable}
            proposedCount={c.proposedCount}
            shiftsLoading={c.shiftsLoading}
            shiftsError={c.shiftsError}
            shiftsFetching={c.shiftsFetching}
            shiftsReady={c.shiftsReady}
            onRetry={c.refetchShifts}
          />
        </div>
      )}

      {/* Board area — the real team grid; the invite hero sits above it until a
          roster exists. Four team states (loading / error / empty / data), plus
          the state where the caller leads no team at all. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {(c.teamsLoading || c.teamLoading) && !c.teamReady ? (
          <TeamSkeleton />
        ) : c.teamError && !c.teamReady ? (
          <TeamLoadRetry onRetry={c.refetchTeam} />
        ) : !c.teamId ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-1 p-8 text-center">
            <p className="text-[17px] text-[var(--color-surface-ink)]">
              {t('roster.noTeamTitle', 'No team to plan')}
            </p>
            <p className="text-[14px] text-[var(--color-surface-mute)]">
              {t('roster.noTeamHint', 'You are not a member or lead of any team yet.')}
            </p>
          </div>
        ) : c.team.length === 0 && !c.hasRoster ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-1 p-8 text-center">
            <p className="text-[17px] text-[var(--color-surface-ink)]">
              {t('roster.emptyTitle', 'No shifts yet')}
            </p>
            <p className="text-[14px] text-[var(--color-surface-mute)]">
              {c.isAuthor
                ? t('roster.team.none', 'No team members on this ward yet.')
                : t('roster.emptyViewerHint', 'The planner has not added shifts yet.')}
            </p>
          </div>
        ) : (
          <>
            {c.isAuthor && !c.hasRoster && (
              <RosterEmptyState
                title={title}
                rangeLabel={rangeLabel}
                dayCount={c.windowDates.length}
                onPlan={() => setTroiOpen(true)}
              />
            )}
            <RosterBoard
              entries={c.entries}
              team={c.team}
              windowDates={c.windowDates}
              isUpdating={boardBusy}
              // A draft is changed through the plan, not cell by cell: a shift
              // row written now would be reconciled away when the draft publishes.
              editable={c.isAuthor && !c.hasDraft}
              onClearCell={c.clearCell}
            />
          </>
        )}
      </div>

      {/* Sticky action bar — planner, once a roster exists. The whole proposal is
          published or discarded by its id, in ONE write: a ward must never read
          half a roster. The PLAN is the unit: it publishes whole or is
          withdrawn whole, so the bar is offered whenever this ward has one. */}
      {c.isAuthor && c.hasRoster && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pb-safe">
          {c.hasDraft ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDiscard(true)}
                disabled={c.isDiscarding || c.isPublishing}
                className="min-h-11 gap-1.5 px-3 text-[14px]"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t('roster.discard', 'Discard proposal')}
              </Button>
              <Button
                size="pill"
                onClick={c.publish}
                disabled={c.isPublishing || c.isDiscarding}
                className="min-h-11 gap-2 text-[15px]"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {c.isPublishing
                  ? t('roster.publishing', 'Publishing…')
                  : t('roster.publish', 'Publish roster')}
              </Button>
            </>
          ) : (
            <>
              <span className="text-[13px] text-[var(--color-surface-mute)]">
                {t('roster.published', 'This roster is published.')}
              </span>
              <Button
                size="pill"
                variant="outline"
                onClick={() => setTroiOpen(true)}
                className="min-h-11 gap-2 text-[15px]"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {t('roster.replan', 'Plan again')}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Discard confirm — every draft of this proposal goes, in one write. */}
      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('roster.discard', 'Discard proposal')}</DialogTitle>
            <DialogDescription>
              {t(
                'roster.discardWarn',
                'All {{count}} proposed shifts are removed together. Published shifts are not touched.',
                { count: c.proposedCount },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDiscard(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => {
                setConfirmDiscard(false)
                c.discard()
              }}
            >
              {t('roster.discardConfirm', 'Discard')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {c.isAuthor && (
        <TroiRefinePanel
          open={troiOpen}
          onOpenChange={setTroiOpen}
          session={session}
          streaming={c.streaming}
          onSend={c.sendChat}
          sendPending={c.chatPending}
        />
      )}
    </div>
  )
}

/** Team query loading (no cache) — a skeleton team grid, not a blank canvas. */
function TeamSkeleton() {
  return (
    <div className="space-y-2 p-4" aria-busy="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-12 w-full rounded-[18px]" />
      ))}
    </div>
  )
}

/** Team query error (no cache) — plain-language + retry (never a dead grid). */
function TeamLoadRetry({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="p-4">
      <p className="text-[15px] text-[var(--color-surface-ink)]" role="alert">
        {t('roster.team.loadError', 'Could not load the ward team.')}{' '}
        <button
          type="button"
          onClick={onRetry}
          className="rounded-sm font-semibold text-[var(--color-primary-500)] underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
        >
          {t('actions.retry', 'Retry')}
        </button>
      </p>
    </div>
  )
}
